import React, { useState } from 'react';
import { Plus, Save, X, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ApiService from '../../services/api';

const ManualBenefitsInput = ({ userId, onBenefitAdded, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'health',
    status: 'Active',
    coverage: '',
    remaining_months: '',
    premium: '',
    expiry_date: '',
    available_amount: '',
    used_amount: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const benefitTypes = [
    { value: 'health', label: 'Health Benefits (TRICARE)' },
    { value: 'education', label: 'Education Benefits (GI Bill)' },
    { value: 'insurance', label: 'Life Insurance (SGLI/FSGLI)' },
    { value: 'housing', label: 'Housing Benefits (BAH/BAS)' },
    { value: 'disability', label: 'VA Disability Benefits' },
    { value: 'retirement', label: 'Retirement Benefits' },
    { value: 'other', label: 'Other Benefits' }
  ];

  const statusOptions = [
    { value: 'Active', label: 'Active', color: 'bg-green-100 text-green-800' },
    { value: 'Pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'Eligible', label: 'Eligible', color: 'bg-blue-100 text-blue-800' },
    { value: 'Expired', label: 'Expired', color: 'bg-red-100 text-red-800' },
    { value: 'Processing', label: 'Processing', color: 'bg-purple-100 text-purple-800' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const benefitData = {
        user_id: userId,
        name: formData.name,
        type: formData.type,
        status: formData.status,
        coverage: formData.coverage || null,
        remaining_months: formData.remaining_months ? parseInt(formData.remaining_months) : null,
        premium: formData.premium || null,
        expiry_date: formData.expiry_date || null,
        available_amount: formData.available_amount || null,
        used_amount: formData.used_amount || null
      };

      const newBenefit = await ApiService.createBenefit(benefitData);
      
      setSuccess(true);
      setTimeout(() => {
        onBenefitAdded(newBenefit);
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error adding benefit:', error);
      alert('Failed to add benefit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card className="border-0 bg-white/95 backdrop-blur-sm">
        <CardContent className="p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Benefit Added Successfully!</h3>
          <p className="text-gray-600">Your benefit information has been saved to your profile.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 bg-white/95 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            <CardTitle className="text-lg">Add Manual Benefit Information</CardTitle>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Benefit Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Benefit Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., TRICARE Prime, GI Bill, SGLI"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Benefit Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Benefit Type *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {benefitTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Coverage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coverage Details
              </label>
              <input
                type="text"
                name="coverage"
                value={formData.coverage}
                onChange={handleInputChange}
                placeholder="e.g., Self + Family, $400,000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Premium */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Premium/Cost
              </label>
              <input
                type="text"
                name="premium"
                value={formData.premium}
                onChange={handleInputChange}
                placeholder="e.g., $27/month, $0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Remaining Months */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remaining Months
              </label>
              <input
                type="number"
                name="remaining_months"
                value={formData.remaining_months}
                onChange={handleInputChange}
                placeholder="e.g., 36"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date
              </label>
              <input
                type="date"
                name="expiry_date"
                value={formData.expiry_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Available Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Amount
              </label>
              <input
                type="text"
                name="available_amount"
                value={formData.available_amount}
                onChange={handleInputChange}
                placeholder="e.g., No Limit, $50,000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Used Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Used Amount
              </label>
              <input
                type="text"
                name="used_amount"
                value={formData.used_amount}
                onChange={handleInputChange}
                placeholder="e.g., $0, $15,000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Any additional information about this benefit..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Help Text */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Manual Entry Guidelines:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Enter information as it appears on your official documents</li>
                  <li>• Use official benefit names (e.g., "TRICARE Prime" not "Tricare")</li>
                  <li>• Leave fields blank if information is not applicable</li>
                  <li>• This information supplements but doesn't replace official records</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading || !formData.name}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? (
                <>
                  <Save className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Benefit
                </>
              )}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="px-6"
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ManualBenefitsInput;